import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { HttpClient } from '@angular/common/http';
import { AvailableLangs, TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';

@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet],
})
export class LanguagesComponent implements OnInit {
    availableLangs: AvailableLangs;
    activeLang: string;
    flagCodes: any;

    private _destroyRef = inject(DestroyRef);
    private _http = inject(HttpClient);

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseNavigationService: FuseNavigationService,
        private _translocoService: TranslocoService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        this.availableLangs = this._translocoService.getAvailableLangs();

        // Subscribe to language changes
        this._translocoService.langChanges$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((activeLang) => {
                // Get the active lang
                this.activeLang = activeLang;

                // Update the navigation
                this._updateNavigation(activeLang);
            });

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            zh: 'CN',
            en: 'US',
            fr: 'FR',
            de: 'DE',
            ja: 'JP',
            ko: 'KR',
            pl: 'PL',
            es: 'ES',
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Persist locally
        localStorage.setItem('pf-lang', lang);

        // Persist to user profile (fire-and-forget)
        this._http.put('api/auth/profile', { language: lang }).subscribe();

        // Set the active lang
        this._translocoService.setActiveLang(lang);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the navigation
     *
     * @param lang
     * @private
     */
    private _updateNavigation(lang: string): void {
        const navComponent =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        if (!navComponent) {
            return;
        }

        const navigation = navComponent.navigation;
        this._translateNavItems(navigation, navComponent);
    }

    private _translateNavItems(items: FuseNavigationItem[], navComponent: FuseVerticalNavigationComponent): void {
        for (const item of items) {
            const key = `nav.${item.id}`;
            this._translocoService
                .selectTranslate(key)
                .pipe(take(1))
                .subscribe((translation) => {
                    if (translation !== key) {
                        item.title = translation;
                        navComponent.refresh();
                    }
                });
            if (item.children) {
                this._translateNavItems(item.children, navComponent);
            }
        }
    }
}
